import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Breadcrumbs, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import storage from '../../../../config/storage';


import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import ARROW_LEFT from '../../../../assets/images/back.svg';

import { useContext, useEffect, useState } from 'react';
import { ProjectContext } from '../../../../providers/projectProvider';
import RadiusButton from '../../../../components/RadiusButton';
import { getServerSummaryList } from '../../../../services/candidateService';

const columns = [
    { id: 'rollNo', label: 'Roll No', minWidth: 140 },
    { id: 'candidateName', label: 'Candidate Name', minWidth: 140 },
    { id: 'examStarted', label: 'Exam Started', minWidth: 140 },
    { id: 'biometricCaptured', label: 'Biometric', minWidth: 140 },
    { id: 'loggedIn', label: 'Logged In', minWidth: 140 },
    { id: 'usedTime', label: 'Used Duration', minWidth: 140 },
    { id: 'remainingTime', label: 'Remaining Duration', minWidth: 140 },
    { id: 'examEnded', label: 'Exam Over', minWidth: 140 },
    { id: 'rPacksUploaded', label: 'Rpacks Uploaded', minWidth: 140 },
    { id: 'logUploaded', label: 'Audit Logs Uploaded', minWidth: 140 },
];
const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT);

export const ServerSummary = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const examMode = storage.getExamMode();
    const isVenueMode = examMode === 'VENUE_BASED';
    const projectName = params?.projectName;
    const [searchParams] = useSearchParams();
    const status = searchParams.get('filter'); // LOGGED_IN, ABSENT, EXAM_STARTED...


    const { projectDetails } = useContext(ProjectContext);
    const [projectMasterId, setProjectMasterId] = useState(null);
    const [serverId, setServerId] = useState(null);
    const [serverName, setServerName] = useState(null);
    const [examHallId, setExamHallId] = useState(null);
    const [maxPages, setMaxPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [candidates, setCandidates] = useState([]);
    const [courseName, setCourseName] = useState(null);


    const handlePageChange = (e, value) => {
        setCurrentPage(value)
    };

    useEffect(() => {
        const projectMasterId = location?.state?.projectMasterId;
        const serverId = location?.state?.serverId;
        const serverName = location?.state?.serverName;
        const examHallId = location?.state?.examHallId;
        const examHallName = location?.state?.examHallName;
        setProjectMasterId(projectMasterId);
        setServerId(serverId);
        setExamHallId(examHallId);
        setServerName(serverName);
    }, [location]);

    useEffect(() => {
        const fetchServerSummary = async () => {
            try {
                const res = await getServerSummaryList(projectMasterId, serverId, examHallId, currentPage, limit, status);
                if (res.code === 'OK') {
                    const totalPages = Math.ceil(res.data.count / limit);
                    setMaxPages(totalPages);
                    setCandidates(res.data.rows);
                    setCourseName(res.data.courseName);
                }
            } catch (error) {
                console.log('There is an error while fetching exam summary', error);
            }
        }
        if (projectMasterId && serverId && examHallId) {
            fetchServerSummary();
        } else {
            // todo
        }
    }, [projectMasterId, serverId, examHallId, currentPage, status])

    const onGoBack = () => {
        if (projectName) {
            navigate(`/${projectName}/exam-summary`, {
                state: {
                    projectMasterId: projectMasterId,
                }
            });
        }
    }

    let filteredColumns = [...columns];

    if (!isVenueMode) {
        filteredColumns = filteredColumns.filter(col =>
            !['biometricCaptured', 'rPacksUploaded', 'logUploaded'].includes(col.id)
        );
    }

    if (status) {
        filteredColumns = filteredColumns.filter(col =>
            !['examStarted', 'loggedIn', 'examEnded'].includes(col.id)
        );
    }

    const renderTableCell = (columnId, candidateIndex) => {
        const candidate = candidates[candidateIndex];
        switch (columnId) {
            case 'rollNo':
                return candidate['rollNo']
            case 'candidateName':
                return candidate['candidateName']
            case 'examStarted':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['examStarted'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['examStarted'] ? 'Yes' : 'No'}
                </Box>
            case 'biometricCaptured':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['biometricCaptured'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['biometricCaptured'] ? 'Yes' : 'No'}
                </Box>
            case 'loggedIn':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['loggedIn'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['loggedIn'] ? 'Yes' : 'No'}
                </Box>
            case 'examEnded':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['examEnded'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['examEnded'] ? 'Yes' : 'No'}
                </Box>
            case 'usedTime':
                return (
                    <Typography>
                        {candidate.usedTime || '00:00:00'}
                    </Typography>
                );

            case 'remainingTime':
                return (
                    <Typography>
                        {candidate.remainingTime || '00:00:00'}
                    </Typography>
                );


            case 'rPacksUploaded':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['rPacksUploaded'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['rPacksUploaded'] ? 'Yes' : 'No'}
                </Box>
            case 'logUploaded':
                return <Box component='span' sx={{
                    cursor: 'default',
                    color: candidate?.['logUploaded'] ? 'success.main' : 'error.main',
                }}>
                    {candidate?.['logUploaded'] ? 'Yes' : 'No'}
                </Box>
            default:
                return null;
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Server Summary</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container>

                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    columnGap: 2,
                    rowGap: 0.5,
                    mb: 3
                }}>
                    <Box>
                        <Typography variant='h6'>
                            Server Id:
                            <Typography component='span' variant='h6' sx={{ ml: 1, color: 'primary.main' }}>{serverName}</Typography>
                        </Typography>
                        <Typography variant='h6' sx={{ mb: 2 }}>
                            Course Name:
                            <Typography component='span' variant='h6' sx={{ ml: 1, color: 'primary.main' }}>
                                {courseName || '-'}
                            </Typography>
                        </Typography>

                    </Box>
                    <RadiusButton sx={{
                        borderRadius: 40,
                        textTransform: 'capitalize',
                        ml: 'auto'
                    }} color="grey"
                        variant='contained'
                        size='large'
                        onClick={onGoBack}
                        startIcon={<img src={ARROW_LEFT} alt='' />}
                    >Go back</RadiusButton>
                </Box>


                <Paper sx={{
                    width: '100%',
                    overflow: 'hidden',
                    bgcolor: 'background.base',
                    maxHeight: 'calc(100vh - 305px)',
                    maxWidth: 'calc(100vw - 140px)',
                    borderRadius: 1,
                    mb: 4
                }}>
                    <TableContainer sx={{
                        px: 1,
                        pb: 1,
                        maxHeight: 'inherit',
                        maxWidth: 'inherit',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1
                    }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {filteredColumns.map((column) => (

                                        <TableCell
                                            sx={{
                                                color: 'primary.main'
                                            }}
                                            key={column.id}
                                            align='center'
                                            style={{ minWidth: column.minWidth }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!candidates?.length
                                    ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                        <TableCell colSpan={11} sx={{ border: 'none', textAlign: 'center' }}>
                                            No records available
                                        </TableCell>
                                    </TableRow>
                                    : candidates.map((candidate, candidateIndex) => {
                                        return <TableRow hover={false} role="checkbox" key={candidateIndex}>
                                            {filteredColumns.map((column) => {
                                                return <TableCell key={column.id} align='center' sx={{
                                                    px: 1.5,
                                                    py: 0.75
                                                }}>
                                                    {renderTableCell(column.id, candidateIndex)}
                                                </TableCell>
                                            })}
                                        </TableRow>
                                    })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Pagination sx={{ mt: 'auto' }} count={maxPages} page={currentPage} onChange={handlePageChange} shape="rounded" />



            </Container>
        </MainContainer>
    );
}
